import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import {
  createCategoryAllocation,
  formatCategoryAllocationUserLabel,
  listActiveMetaUsers,
  listCaseRequestRoles,
  listStateAttorneys,
  listSupervisingOfficers,
  searchCategoryAllocations,
  type CaseRequestRoleOption,
  type CategoryAllocationUserOption,
  type UserCategoryListRow,
} from '../../services/categoryAllocationSetup';
import { getFileCategories, getSubFileCategories, type LookupItem } from '../../services/criminalCaseRequests';
import { formatAuditDateTime } from '../../utils/dateFormat';
import { fltSelClass } from '../../utils/alSelClass';
import { consumeFlashToast } from '../../utils/flashToast';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';

export default function UserCategoriesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<UserCategoryListRow[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftFileCategoryId, setDraftFileCategoryId] = useState('');
  const [draftSubFileCategoryId, setDraftSubFileCategoryId] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [draftUserRoleId, setDraftUserRoleId] = useState('');
  const [draftSupervisorId, setDraftSupervisorId] = useState('');
  const [draftStateAttorneyId, setDraftStateAttorneyId] = useState('');

  const [fileCategoryId, setFileCategoryId] = useState('');
  const [subFileCategoryId, setSubFileCategoryId] = useState('');
  const [userId, setUserId] = useState('');
  const [userRoleId, setUserRoleId] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [stateAttorneyId, setStateAttorneyId] = useState('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [draftSubFileCategories, setDraftSubFileCategories] = useState<LookupItem[]>([]);
  const [activeUsers, setActiveUsers] = useState<CategoryAllocationUserOption[]>([]);
  const [roles, setRoles] = useState<CaseRequestRoleOption[]>([]);
  const [supervisors, setSupervisors] = useState<CategoryAllocationUserOption[]>([]);
  const [stateAttorneys, setStateAttorneys] = useState<CategoryAllocationUserOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [subOptionsLoading, setSubOptionsLoading] = useState(false);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setOptionsLoading(true);
    void Promise.all([
      getFileCategories(),
      listActiveMetaUsers(),
      listCaseRequestRoles(),
      listSupervisingOfficers(),
      listStateAttorneys(),
    ]).then(([cats, users, roleRes, sups, stateAttorneyRes]) => {
      if (!alive) return;
      setFileCategories(cats.success ? cats.data : []);
      setActiveUsers(users.success ? users.data : []);
      setRoles(roleRes.success ? roleRes.data : []);
      setSupervisors(sups.success ? sups.data : []);
      setStateAttorneys(stateAttorneyRes.success ? stateAttorneyRes.data : []);
      setOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!draftFileCategoryId) {
      setDraftSubFileCategories([]);
      setDraftSubFileCategoryId('');
      return;
    }
    let alive = true;
    setSubOptionsLoading(true);
    void getSubFileCategories(draftFileCategoryId).then((res) => {
      if (!alive) return;
      setDraftSubFileCategories(res.success ? res.data : []);
      setSubOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [draftFileCategoryId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const parsedRoleId = userRoleId.trim() ? Number(userRoleId) : undefined;
      const res = await searchCategoryAllocations({
        fileCategoryId: fileCategoryId || undefined,
        subFileCategoryId: subFileCategoryId || undefined,
        userId: userId || undefined,
        userRoleId: parsedRoleId != null && Number.isFinite(parsedRoleId) ? parsedRoleId : undefined,
        supervisorId: supervisorId || undefined,
        stateAttorneyId: stateAttorneyId || undefined,
      });
      if (!res.success) throw new Error(res.message);
      setRows(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.userCategories.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fileCategoryId, reloadToken, stateAttorneyId, subFileCategoryId, supervisorId, t, userId, userRoleId]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(uiPage, totalPages);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const columns = useMemo<Column<UserCategoryListRow>[]>(() => {
    return [
      { key: 'fileCategory', header: t('admin.shared.fileCategory'), getValue: (r) => r.fileCategory },
      {
        key: 'subFileCategory',
        header: t('admin.shared.subFileCategory'),
        getValue: (r) => r.subFileCategory,
        render: (r) => <span style={{ color: r.subFileCategory === EMPTY ? 'var(--t3)' : undefined }}>{r.subFileCategory}</span>,
      },
      {
        key: 'userName',
        header: t('admin.userCategoryForm.userNames'),
        getValue: (r) => r.userName,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.userName}</span>,
      },
      { key: 'userRoles', header: t('profile.userRoles'), getValue: (r) => r.userRoles },
      { key: 'supervisor', header: t('admin.shared.supervisor'), getValue: (r) => r.supervisor },
      { key: 'stateAttorney', header: t('tasks.assign.stateAttorney'), getValue: (r) => r.stateAttorney },
      {
        key: 'lastUpdatedAt',
        header: t('profile.lastUpdatedAt'),
        getValue: (r) => r.lastUpdatedAt,
        render: (r) => (
          <span style={{ color: 'var(--t2)' }}>{formatAuditDateTime(r.lastUpdatedAt) || r.lastUpdatedAt || EMPTY}</span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <Link
              className="act-b"
              to={`/admin/user-categories/${encodeURIComponent(r.categoryAllocationId)}/edit`}
              data-tip={t('admin.userCategories.editUserCategory')}
              aria-label={t('admin.userCategories.editUserCategory')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </Link>
          </div>
        ),
      },
    ];
  }, [t]);

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        <Link to="/admin/intranet-users">{t('admin.intranetUsers.title')}</Link>
        <span className="bc-sep">/</span>
        {t('admin.userCategories.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('admin.userCategories.title')}</div>
        </div>
        <Link to="/admin/user-categories/new" className="btn-create" style={{ textDecoration: 'none' }}>
          {t('admin.userCategories.addUserCategory')}
        </Link>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div className="flt-grid">
          <div className="flt-f">
            <div className="flt-l">{t('admin.shared.fileCategory')}</div>
            <select
              className={fltSelClass(draftFileCategoryId)}
              value={draftFileCategoryId}
              disabled={optionsLoading}
              onChange={(e) => {
                setDraftFileCategoryId(e.target.value);
                setDraftSubFileCategoryId('');
              }}
            >
              {optionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : fileCategories.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.userCategories.selectFileCategory')}
                  </option>
                  {fileCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('admin.shared.subFileCategory')}</div>
            <select
              className={fltSelClass(draftSubFileCategoryId)}
              value={draftSubFileCategoryId}
              disabled={!draftFileCategoryId || subOptionsLoading}
              onChange={(e) => setDraftSubFileCategoryId(e.target.value)}
            >
              {!draftFileCategoryId ? (
                <option value="" disabled hidden>
                  {t('admin.userCategories.selectSubFileCategory')}
                </option>
              ) : subOptionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : draftSubFileCategories.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.userCategories.selectSubFileCategory')}
                  </option>
                  {draftSubFileCategories.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('admin.userCategoryForm.userNames')}</div>
            <select
              className={fltSelClass(draftUserId)}
              value={draftUserId}
              disabled={optionsLoading}
              onChange={(e) => setDraftUserId(e.target.value)}
            >
              {optionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : activeUsers.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.userCategories.selectUserName')}
                  </option>
                  {activeUsers.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {formatCategoryAllocationUserLabel(u)}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('profile.userRole')}</div>
            <select
              className={fltSelClass(draftUserRoleId)}
              value={draftUserRoleId}
              disabled={optionsLoading}
              onChange={(e) => setDraftUserRoleId(e.target.value)}
            >
              {optionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : roles.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.userCategories.selectUserRole')}
                  </option>
                  {roles.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('admin.shared.supervisor')}</div>
            <select
              className={fltSelClass(draftSupervisorId)}
              value={draftSupervisorId}
              disabled={optionsLoading}
              onChange={(e) => setDraftSupervisorId(e.target.value)}
            >
              {optionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : supervisors.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.userCategories.selectSupervisor')}
                  </option>
                  {supervisors.map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {s.displayName.trim() || formatCategoryAllocationUserLabel(s)}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('tasks.assign.stateAttorney')}</div>
            <select
              className={fltSelClass(draftStateAttorneyId)}
              value={draftStateAttorneyId}
              disabled={optionsLoading}
              onChange={(e) => setDraftStateAttorneyId(e.target.value)}
            >
              {optionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : stateAttorneys.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.userCategories.selectStateAttorney')}
                  </option>
                  {stateAttorneys.map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {s.displayName.trim() || formatCategoryAllocationUserLabel(s)}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>
        <div className="flt-actions">
          <button
            className="flt-btn"
            type="button"
            onClick={() => {
              setDraftFileCategoryId('');
              setDraftSubFileCategoryId('');
              setDraftUserId('');
              setDraftUserRoleId('');
              setDraftSupervisorId('');
              setDraftStateAttorneyId('');
              setFileCategoryId('');
              setSubFileCategoryId('');
              setUserId('');
              setUserRoleId('');
              setSupervisorId('');
              setStateAttorneyId('');
              setUiPage(1);
              setPageSize(5);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.reset')}
          </button>
          <button
            className="flt-btn pr"
            type="button"
            onClick={() => {
              setFileCategoryId(draftFileCategoryId);
              setSubFileCategoryId(draftSubFileCategoryId);
              setUserId(draftUserId);
              setUserRoleId(draftUserRoleId);
              setSupervisorId(draftSupervisorId);
              setStateAttorneyId(draftStateAttorneyId);
              setUiPage(1);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('admin.userCategories.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={5}
        rowsPerPageOptions={[5, 10, 15]}
        footerCountNoun={t('admin.userCategories.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: rows.length,
          totalPages,
          page: safePage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
